import type { SkinId } from "../config/gameplay-config";

export interface Vector2 {
  readonly x: number;
  readonly y: number;
}

export interface PlayerCollisionCircle {
  readonly radius: number;
}

export interface PlayerMovementState {
  readonly alive: boolean;
  readonly collision: PlayerCollisionCircle;
  readonly directionRadians: number;
  readonly growth: number;
  readonly level: number;
  readonly position: Vector2;
  readonly selectedSkinId: SkinId;
  readonly size: number;
  readonly speed: number;
}

export interface PlayerMovementInitialState {
  readonly directionRadians: number;
  readonly level: number;
  readonly position: Vector2;
  readonly selectedSkinId: SkinId;
  readonly size: number;
  readonly speed: number;
}

const requireFinite = (value: number, name: string): void => {
  if (!Number.isFinite(value)) {
    throw new RangeError(`${name} must be finite`);
  }
};

const requirePositive = (value: number, name: string): void => {
  requireFinite(value, name);
  if (value <= 0) {
    throw new RangeError(`${name} must be positive`);
  }
};

const requirePositiveInteger = (value: number, name: string): void => {
  requirePositive(value, name);
  if (!Number.isInteger(value)) {
    throw new RangeError(`${name} must be an integer`);
  }
};

export const createPlayerMovementState = (
  initial: PlayerMovementInitialState,
): PlayerMovementState => {
  requireFinite(initial.position.x, "Player position x");
  requireFinite(initial.position.y, "Player position y");
  requireFinite(initial.directionRadians, "Player direction");
  requirePositive(initial.speed, "Player speed");
  requirePositive(initial.size, "Player size");
  requirePositiveInteger(initial.level, "Player level");
  return {
    alive: true,
    collision: { radius: initial.size },
    directionRadians: initial.directionRadians,
    growth: 0,
    level: initial.level,
    position: { ...initial.position },
    selectedSkinId: initial.selectedSkinId,
    size: initial.size,
    speed: initial.speed,
  };
};

export const stepPlayerMovement = (
  state: PlayerMovementState,
  stepMilliseconds: number,
): PlayerMovementState => {
  requirePositive(stepMilliseconds, "Movement step");
  const distance = state.speed * (stepMilliseconds / 1_000);
  return {
    ...state,
    position: {
      x: state.position.x + Math.cos(state.directionRadians) * distance,
      y: state.position.y + Math.sin(state.directionRadians) * distance,
    },
  };
};
